import React from "react";
import clsx from "clsx";

import type { BoxProps } from "@/ui/atoms/Box";
import Box from "@/ui/atoms/Box";
import ProgressBar from "@/ui/molecules/ProgressBar";
import { testIds as testIdsFileInput } from "@/ui/organisms/FileUpload";
import type { LayoutBoxCenteredProps } from "@/ui/layouts/LayoutBoxCentered";
import LayoutBoxCentered from "@/ui/layouts/LayoutBoxCentered";
import CoAUpload from "@/ui/organisms/CoAUpload";
export type ViewCoAUploadParams = {
    useState: () => {
        uploadCoAStatus: "idle" | "pending" | "resolved" | "rejected";
        selectedFileName?: string | null;
        progressPercentage: number | null;
        progressMessage: string | null;
        error: string | null;
    };
    useCases: () => {
        onMount: () => void;
        onUploadCoATriggered: (params: {
            files: FileList;
            coaId: number | null;
        }) => Promise<void>;
    };
};
export type ViewCoAUploadProps = Omit<BoxProps, "children"> & {
    contentAboveBoxWhenUploadIsIdle?: LayoutBoxCenteredProps["contentAboveBox"];
    LayoutBoxCenteredProps?: LayoutBoxCenteredProps;
    coaId: number | null;
};

export const testIds = {
    ...testIdsFileInput,
    main: "ViewCoAUpload",
    closeButton: "ViewCoAUploadCloseButton",
};
export const createViewCoAUpload = ({
    useState,
    useCases,
}: ViewCoAUploadParams) => {
    const ViewCoAUpload = (props: ViewCoAUploadProps) => {
        const {
            sx,
            className,
            contentAboveBoxWhenUploadIsIdle,
            LayoutBoxCenteredProps,
            coaId,
            ...otherProps
        } = props;

        const { onUploadCoATriggered, onMount } = useCases();

        const {
            uploadCoAStatus,
            selectedFileName,
            error,
            progressPercentage,
            progressMessage,
        } = useState();
        const idle = uploadCoAStatus === "idle";
        const uploading = uploadCoAStatus === "pending";
        const success = uploadCoAStatus === "resolved";
        React.useEffect(() => {
            onMount?.();
        }, [onMount]);

        const handleFileDrop = React.useCallback(
            (e: React.DragEvent<HTMLDivElement>) => {
                e.preventDefault();
                e.stopPropagation();
                if (e.dataTransfer === null) return;
                const files = e.dataTransfer.files;
                void onUploadCoATriggered({ files, coaId });
            },
            [onUploadCoATriggered, coaId],
        );

        const handleFileInput = React.useCallback(
            (e: React.ChangeEvent<HTMLInputElement>) => {
                const files = e.target.files;
                if (files === null) return;
                void onUploadCoATriggered({ files, coaId });
            },
            [onUploadCoATriggered, coaId],
        );

        const uploadingFile = () => {
            return (
                <Box
                    sx={{
                        padding: "80px",
                    }}
                >
                    <Box
                        id="processing-wrapper"
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                        }}
                    >
                        <Box
                            className="status-header"
                            sx={{
                                fontSize: "2rem",
                                fontWeight: 600,
                                color: "var(--text-color-dark)",
                            }}
                        >
                            The file is being processed...
                        </Box>
                        <Box
                            component={"p"}
                            className="process-subheader"
                            sx={{
                                color: "var(--proda-black-10)",
                                fontWeight: "400 !important",
                                width: "55%",
                                marginTop: "16px",
                                marginBottom: "16px",
                            }}
                        >
                            Do not close the window while the file is being
                            processed.
                        </Box>
                    </Box>

                    <ProgressBar
                        label={selectedFileName}
                        subLabel={progressMessage}
                        progressPercentage={progressPercentage ?? 0}
                    />
                </Box>
            );
        };

        return (
            <Box
                data-testid={testIds.main}
                className={clsx([testIds.main, className])}
                sx={{
                    ...sx,
                }}
                {...otherProps}
            >
                <LayoutBoxCentered
                    contentAboveBox={
                        uploadCoAStatus === "idle"
                            ? contentAboveBoxWhenUploadIsIdle
                            : null
                    }
                    sx={{
                        width: "35%",
                        "@media (max-width: 600px)": {
                            width: "85%",
                        },
                        minWidth: "500px",
                        ...LayoutBoxCenteredProps?.sx,
                    }}
                    {...LayoutBoxCenteredProps}
                >
                    <Box
                        sx={{
                            width: "100%",
                            height: "100%",
                            boxSizing: "border-box",
                            fontSize: "18px",
                            color: "var(--text-color-dark)",
                        }}
                    >
                        {(idle || error !== null) && (
                            <CoAUpload
                                error={error}
                                handleFileDrop={handleFileDrop}
                                handleFileInput={handleFileInput}
                            />
                        )}
                        {(uploading || success) && uploadingFile()}
                    </Box>
                </LayoutBoxCentered>
            </Box>
        );
    };
    return ViewCoAUpload;
};
